import React, { useState, useEffect } from 'react';
import { 
  SaveOutlined, 
  LoadingOutlined, 
  HomeOutlined, 
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { apiClient } from '../lib/api';
import type { Homepage, Section, FeaturedSection } from '../lib/api';

const HomepageEditor: React.FC = () => {
  const [homepage, setHomepage] = useState<Homepage | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [featuredSections, setFeaturedSections] = useState<FeaturedSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load homepage data, available sections, and featured sections in parallel
      const [homepageData, sectionsData, featuredSectionsData] = await Promise.all([
        apiClient.getHomepage(),
        apiClient.getSections({ limit: 100, published: true }),
        apiClient.getFeaturedSections()
      ]);
      
      setHomepage(homepageData);
      setSections(sectionsData.data || []);
      setFeaturedSections(featuredSectionsData.data || []);
    } catch (err) {
      console.error('Failed to load homepage data:', err);
      setError('Failed to load homepage data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Note: Since we're now managing featured sections through dedicated API,
    // this save function is mainly for future homepage content updates
    setSuccess('Featured sections are saved automatically!');
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleFeaturedSectionAdd = async (sectionId: string) => {
    try {
      setError(null);
      await apiClient.addFeaturedSection(sectionId);
      
      // Reload data to get updated featured sections
      await loadData();
      setSuccess('Section added to featured list!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to add featured section:', err);
      setError('Failed to add section to featured list. Please try again.');
    }
  };

  const handleFeaturedSectionRemove = async (sectionId: string) => {
    try {
      setError(null);
      await apiClient.removeFeaturedSection(sectionId);
      
      // Reload data to get updated featured sections
      await loadData();
      setSuccess('Section removed from featured list!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to remove featured section:', err);
      setError('Failed to remove section from featured list. Please try again.');
    }
  };

  const getAvailableSections = () => {
    const featuredSectionIds = featuredSections.map(fs => fs.sectionId);
    return sections.filter(section => !featuredSectionIds.includes(section.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoadingOutlined className="text-4xl text-primary-500 mb-4" />
          <p className="text-gray-6">Loading homepage data...</p>
        </div>
      </div>
    );
  }

  if (!homepage) {
    return (
      <div className="p-6">
        <div className="admin-card text-center">
          <p className="text-red-500 mb-4">Failed to load homepage data</p>
          <button
            onClick={loadData}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <HomeOutlined className="text-2xl text-gray-7" />
          <div>
            <h1 className="text-2xl font-bold text-gray-9">Homepage Management</h1>
            <p className="text-gray-6">Manage your homepage content and layout</p>
          </div>
        </div>
        
        <button
          onClick={handleSave}
          className="btn-primary flex items-center space-x-2"
        >
          <SaveOutlined />
          <span>Refresh Data</span>
        </button>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-600">{success}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Hero Section */}
        {/* <div className="admin-card">
          <h2 className="text-xl font-semibold text-gray-9 mb-4 flex items-center space-x-2">
            <FileTextOutlined />
            <span>Hero Section</span>
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-7 mb-2">
                Hero Title
              </label>
              <input
                type="text"
                value={homepage.heroTitle}
                onChange={(e) => handleInputChange('heroTitle', e.target.value)}
                className="form-input w-full"
                placeholder="Enter hero title..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-7 mb-2">
                Hero Subtitle
              </label>
              <textarea
                value={homepage.heroSubtitle}
                onChange={(e) => handleInputChange('heroSubtitle', e.target.value)}
                rows={3}
                className="form-textarea w-full"
                placeholder="Enter hero subtitle..."
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-7 mb-2 flex items-center space-x-1">
                  <PictureOutlined />
                  <span>Hero Image URL (Optional)</span>
                </label>
                <input
                  type="url"
                  value={homepage.heroImageUrl || ''}
                  onChange={(e) => handleInputChange('heroImageUrl', e.target.value)}
                  className="form-input w-full"
                  placeholder="https://example.com/hero-image.jpg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-7 mb-2 flex items-center space-x-1">
                  <VideoCameraOutlined />
                  <span>Hero Video URL (Optional)</span>
                </label>
                <input
                  type="url"
                  value={homepage.heroVideoUrl || ''}
                  onChange={(e) => handleInputChange('heroVideoUrl', e.target.value)}
                  className="form-input w-full"
                  placeholder="https://example.com/hero-video.mp4"
                />
              </div>
            </div>
          </div>
        </div> */}

        {/* Navigation Text */}
        {/* <div className="admin-card">
          <h2 className="text-xl font-semibold text-gray-9 mb-4">
            Navigation Text
          </h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-7 mb-2">
              Navigation Indicator Text
            </label>
            <input
              type="text"
              value={homepage.navigationText}
              onChange={(e) => handleInputChange('navigationText', e.target.value)}
              className="form-input w-full"
              placeholder="Enter navigation text..."
            />
            <p className="text-sm text-gray-5 mt-1">
              This text appears with the navigation dots on the homepage
            </p>
          </div>
        </div> */}

        {/* Featured Sections */}
        <div className="admin-card">
          <h2 className="text-xl font-semibold text-gray-9 mb-4">
            Featured Sections
          </h2>
          
          {/* Current Featured Sections */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-8 mb-3">
              Currently Featured ({featuredSections.length})
            </h3>
            
            {featuredSections.length === 0 ? (
              <div className="text-gray-5 text-center py-8 border-2 border-dashed border-gray-3 rounded-lg">
                No featured sections selected
              </div>
            ) : (
              <div className="space-y-3">
                {featuredSections.map((featuredSection, index) => {
                  return (
                    <div key={featuredSection.sectionId} className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <span className="text-sm font-medium text-gray-5">#{index + 1}</span>
                        <div>
                          <h4 className="font-medium text-gray-9">{featuredSection.title}</h4>
                          <p className="text-sm text-gray-6">{featuredSection.shortDescription}</p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => handleFeaturedSectionRemove(featuredSection.sectionId)}
                        className="text-red-500 hover:text-red-600 p-2"
                        title="Remove from featured"
                      >
                        <DeleteOutlined />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Available Sections to Add */}
          <div>
            <h3 className="text-lg font-medium text-gray-8 mb-3">
              Add Sections ({getAvailableSections().length} available)
            </h3>
            
            {getAvailableSections().length === 0 ? (
              <div className="text-gray-5 text-center py-6 border-2 border-dashed border-gray-3 rounded-lg">
                All published sections are already featured
              </div>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {getAvailableSections().map((section) => (
                  <div key={section.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-9">{section.title}</h4>
                      <p className="text-sm text-gray-6">{section.shortDescription}</p>
                    </div>
                    
                    <button
                      onClick={() => handleFeaturedSectionAdd(section.id)}
                      className="text-primary-500 hover:text-primary-600 p-2"
                      title="Add to featured"
                    >
                      <PlusOutlined />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Preview Info */}
        <div className="admin-card bg-blue-50 border-blue-200">
          <h2 className="text-xl font-semibold text-blue-900 mb-4">
            Preview Information
          </h2>
          
          <div className="text-sm text-blue-800 space-y-2">
            <p>• Changes will be reflected on the public homepage immediately after saving</p>
            <p>• Featured sections appear in the order listed above</p>
            <p>• Hero image and video are optional - leave blank to use default styling</p>
            <p>• Only published sections can be featured</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomepageEditor;
